import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Stack,
  Divider,
  Button,
  CircularProgress,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Chip,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
  Snackbar,
  Paper,
} from "@mui/material";
import {
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ConfirmationNumber as TicketIcon,
  Discount as DiscountIcon,
  CheckCircle as CheckCircleIcon,
  Map as MapIcon,
  OpenInNew as OpenInNewIcon,
  Copyright as CopyrightIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  ChildCare as ChildCareIcon,
} from "@mui/icons-material";

// Import image from assets folder
import bannerImage from "../assets/children.jpeg";
import logo from "../assets/logo.png";

// API Base URL from environment variables

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL


// // Paystack Public Key
// const PAYSTACK_PUBLIC_KEY = "pk_live_6036f4cdb3cc4ceeda6e2977b97f74161677eb3d"; // Replace with your actual key

// Elegant Red & Black Brand Palette
const colors = {
  primary: {
    main: "#B22222",
    dark: "#8B0000",
    gradient: "linear-gradient(135deg, #B22222 0%, #8B0000 100%)",
    glow: "rgba(178, 34, 34, 0.4)",
    light: "rgba(178, 34, 34, 0.1)",
  },
  background: {
    main: "#FFFFFF",
    soft: "#FCF8F8",
  },
  text: {
    secondary: "#555555",
  },
};

// Preloader Component
const ElegantePreloader = () => {
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "#000000",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10000,
      }}
    >
      <Box sx={{ position: "relative", mb: 5 }}>
        <CircularProgress
          size={100}
          thickness={2}
          sx={{ color: colors.primary.main, animationDuration: "1.5s" }}
        />
        <CircularProgress
          size={80}
          thickness={2}
          sx={{
            color: colors.primary.dark,
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            animationDuration: "2s",
            opacity: 0.7,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 12,
            height: 12,
            borderRadius: "50%",
            backgroundColor: colors.primary.main,
            animation: "pulseDot 1.5s ease-in-out infinite",
            "@keyframes pulseDot": {
              "0%": { transform: "translate(-50%, -50%) scale(1)", opacity: 1 },
              "50%": { transform: "translate(-50%, -50%) scale(1.5)", opacity: 0.6 },
              "100%": { transform: "translate(-50%, -50%) scale(1)", opacity: 1 },
            },
          }}
        />
      </Box>
      <Box
        component="img"
        src={logo}
        alt="Eleganté"
        sx={{ height: 60, objectFit: "contain", mb: 1 }}
        onError={(e) => {
          e.target.style.display = "none";
          const fallback = document.createElement("div");
          fallback.innerHTML = '<span style="font-size: 2rem; font-weight: 800; background: linear-gradient(135deg, #B22222 0%, #8B0000 100%); background-clip: text; -webkit-background-clip: text; color: transparent;">Eleganté</span>';
          e.target.parentElement.appendChild(fallback);
        }}
      />
      <Typography
        variant="subtitle1"
        sx={{
          color: "#999999",
          letterSpacing: "3px",
          textTransform: "uppercase",
          fontSize: "0.75rem",
          mt: 1,
        }}
      >
        Children's Day Party 
      </Typography>
    </Box>
  );
};

// Ticket Selection Dialog Component
const TicketDialog = ({ open, onClose, onConfirm }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [quantity, setQuantity] = useState(1);
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(false);
  const ticketPrice = 30000;
  const discountAmount = 5000;
  
  const subtotal = quantity * ticketPrice;
  const discount = discountApplied ? discountAmount : 0;
  const total = subtotal - discount;
  
  const handleQuantityChange = (delta) => {
    const newQuantity = quantity + delta;
    if (newQuantity >= 1 && newQuantity <= 20) {
      setQuantity(newQuantity);
    }
  };
  
  // const applyDiscount = () => {
  //   if (discountCode.toUpperCase() === "KIDS2026") {
  //     setDiscountApplied(true);
  //   } else {
  //     alert("Invalid discount code. Try 'KIDS2026'");
  //   }
  // };
  
  const handleConfirm = () => {
    onConfirm({
      quantity,
      ticketPrice,
      discount: discountApplied ? discountAmount : 0,
      total,
      discountCode: discountApplied ? discountCode : null,
    });
    onClose();
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          overflow: "hidden",
          margin: isMobile ? 0 : "auto",
          width: isMobile ? "100%" : "auto",
          maxHeight: isMobile ? "100%" : "90vh",
        },
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: colors.primary.main, 
        color: "white", 
        py: isMobile ? 2 : 2.5,
        px: isMobile ? 2 : 3,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TicketIcon />
          <Typography variant="h6" fontWeight={700}>Choose Tickets</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        p: isMobile ? 2 : 3, 
        bgcolor: "#F9F9F9",
        flex: 1,
        overflowY: "auto",
      }}>
        <Card sx={{ 
          mb: isMobile ? 2 : 3, 
          borderRadius: 3, 
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}>
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            <Box sx={{ 
              display: "flex", 
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between", 
              alignItems: isMobile ? "stretch" : "flex-start", 
              gap: isMobile ? 2 : 0,
              mb: 2 
            }}>
              <Box>
                <Typography variant="h6" fontWeight={700} fontSize={isMobile ? "1rem" : "1.25rem"}>
                  Kids Entry Pass
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ₦{ticketPrice.toLocaleString()}.00 • Adult entry is free
                </Typography>
              </Box>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: isMobile ? "space-between" : "flex-end",
                gap: 2,
                minWidth: isMobile ? "auto" : 120,
              }}>
                <Typography variant="body2" fontWeight={500} sx={{ display: isMobile ? "block" : "none" }}>
                  Quantity:
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton 
                    size={isMobile ? "medium" : "small"} 
                    onClick={() => handleQuantityChange(-1)}
                    sx={{ border: "1px solid #ddd", borderRadius: 2 }}
                  >
                    <RemoveIcon fontSize={isMobile ? "small" : "small"} />
                  </IconButton>
                  <Typography variant="h6" fontWeight={600} sx={{ minWidth: 40, textAlign: "center" }}>
                    {quantity}
                  </Typography>
                  <IconButton 
                    size={isMobile ? "medium" : "small"} 
                    onClick={() => handleQuantityChange(1)}
                    sx={{ border: "1px solid #ddd", borderRadius: 2, bgcolor: colors.primary.light }}
                  >
                    <AddIcon fontSize={isMobile ? "small" : "small"} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Box sx={{ mb: isMobile ? 2 : 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <DiscountIcon fontSize="small" sx={{ color: colors.primary.main }} />
            DISCOUNT CODE:
          </Typography>
          <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter code (KIDS2026)"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              disabled={discountApplied}
              InputProps={{
                sx: { borderRadius: 2, bgcolor: "white" }
              }}
            />
            <Button
              variant="contained"
              // onClick={applyDiscount}
              disabled={discountApplied || !discountCode}
              fullWidth={isMobile}
              sx={{
                bgcolor: colors.primary.main,
                textTransform: "none",
                borderRadius: 2,
                py: isMobile ? 1 : "auto",
                "&:hover": { bgcolor: colors.primary.dark },
              }}
            >
              Apply
            </Button>
          </Box>
          {discountApplied && (
            <Chip
              icon={<CheckCircleIcon />}
              label="₦5,000 discount applied!"
              size="small"
              sx={{ mt: 1, bgcolor: "#4CAF50", color: "white", width: isMobile ? "100%" : "auto" }}
            />
          )}
        </Box>
        
        <Divider sx={{ my: isMobile ? 1.5 : 2 }} />
        
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: isMobile ? 1.5 : 2 }}>
          Summary
        </Typography>
        
        <Stack spacing={isMobile ? 1 : 1.5} sx={{ mb: isMobile ? 1.5 : 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2" color="text.secondary">Kids Pass ({quantity})</Typography>
            <Typography variant="body2" fontWeight={500}>₦{subtotal.toLocaleString()}</Typography>
          </Box>
          {discountApplied && (
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" color="success.main">Discount (KIDS2026)</Typography>
              <Typography variant="body2" color="success.main">-₦{discount.toLocaleString()}</Typography>
            </Box>
          )}
          <Divider />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
            <Typography variant={isMobile ? "h5" : "h6"} fontWeight={800} sx={{ color: colors.primary.main }}>
              ₦{total.toLocaleString()}
            </Typography>
          </Box>
        </Stack>
        
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleConfirm}
          sx={{
            bgcolor: colors.primary.main,
            py: isMobile ? 1.5 : 1.5,
            borderRadius: 3,
            fontWeight: 700,
            fontSize: isMobile ? "0.9rem" : "1rem",
            "&:hover": { bgcolor: colors.primary.dark },
          }}
        >
          Confirm & Continue
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// Attendee Information Form Dialog - COMPLETE FIXED VERSION
const AttendeeFormDialog = ({ open, onClose, ticketDetails, onPaymentInitiated }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    child_age_range: "",
    adults_accompanying: "",
  });
  const [errors, setErrors] = useState({});

  const ageRanges = [
    "0-2 years",
    "3-5 years",
    "6-9 years",
    "10-13 years",
    "14-17 years",
  ];

  const adultOptions = ["0", "1", "2", "3", "4", "5+"];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) newErrors.first_name = "First name is required";
    if (!formData.last_name.trim()) newErrors.last_name = "Last name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[0-9]{10,15}$/.test(formData.phone.replace(/[^0-9]/g, ''))) {
      newErrors.phone = "Invalid phone number";
    }
    if (!formData.child_age_range) newErrors.child_age_range = "Please select child's age range";
    if (!formData.adults_accompanying) newErrors.adults_accompanying = "Please select number of adults";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const handleSubmit = async () => {
  if (!validateForm()) return;
  
  if (!ticketDetails) {
    console.error("Ticket details are missing");
    alert("Please select tickets first");
    return;
  }
  
  setLoading(true);
  
  try {
    const payload = {
      ...formData,
      ticket_quantity: ticketDetails.quantity,
      ticket_price: ticketDetails.ticketPrice,
      discount_amount: ticketDetails.discount,
      total_amount: ticketDetails.total,
      discount_code: ticketDetails.discountCode,
    };
    
    console.log("Initializing payment with payload:", payload);
    console.log("API URL:", `${API_BASE_URL}/initialize_payment.php`);
    
    const response = await fetch(`${API_BASE_URL}/initialize_payment.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      mode: "cors",
      body: JSON.stringify(payload),
    });
    
    console.log("Response status:", response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response:", errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log("Payment initialization response:", result);
    
    if (result.success && result.data && result.data.authorization_url) {
      window.location.href = result.data.authorization_url;
    } else {
      throw new Error(result.message || "Failed to initialize payment");
    }
  } catch (error) {
    console.error("Payment initialization error:", error);
    alert(`Error: ${error.message}. Please try again.`);
  } finally {
    setLoading(false);
  }
};

  if (!ticketDetails && open) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: colors.primary.main, color: "white" }}>
          Loading...
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Preparing booking form...</Typography>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 4,
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: colors.primary.main, 
        color: "white", 
        py: isMobile ? 2 : 2.5,
        px: isMobile ? 2 : 3,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PersonIcon />
          <Typography variant="h6" fontWeight={700}>Attendee Information</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        p: isMobile ? 2 : 3, 
        bgcolor: "#F9F9F9",
        maxHeight: "70vh",
        overflowY: "auto",
      }}>
        <Stack spacing={2.5}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Please provide accurate information. Your ticket will be sent to your email.
          </Alert>
          
          <Box sx={{ display: "flex", gap: 2, flexDirection: isMobile ? "column" : "row" }}>
            <TextField
              fullWidth
              label="First Name"
              value={formData.first_name}
              onChange={handleChange("first_name")}
              error={!!errors.first_name}
              helperText={errors.first_name}
              required
              sx={{ bgcolor: "white", borderRadius: 1 }}
            />
            <TextField
              fullWidth
              label="Last Name"
              value={formData.last_name}
              onChange={handleChange("last_name")}
              error={!!errors.last_name}
              helperText={errors.last_name}
              required
              sx={{ bgcolor: "white", borderRadius: 1 }}
            />
          </Box>
          
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={formData.email}
            onChange={handleChange("email")}
            error={!!errors.email}
            helperText={errors.email}
            required
            sx={{ bgcolor: "white", borderRadius: 1 }}
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: "#999" }} />,
            }}
          />
          
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phone}
            onChange={handleChange("phone")}
            error={!!errors.phone}
            helperText={errors.phone}
            required
            placeholder="e.g., 08012345678"
            sx={{ bgcolor: "white", borderRadius: 1 }}
            InputProps={{
              startAdornment: <PhoneIcon sx={{ mr: 1, color: "#999" }} />,
            }}
          />
          
          <FormControl required error={!!errors.child_age_range} sx={{ bgcolor: "white", borderRadius: 1, p: 1 }}>
            <FormLabel sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ChildCareIcon sx={{ color: colors.primary.main }} />
              Child's Age Range
            </FormLabel>
            <RadioGroup
              value={formData.child_age_range}
              onChange={handleChange("child_age_range")}
              row={!isMobile}
              sx={{ flexWrap: "wrap", mt: 1 }}
            >
              {ageRanges.map((range) => (
                <FormControlLabel
                  key={range}
                  value={range}
                  control={<Radio sx={{ color: colors.primary.main, "&.Mui-checked": { color: colors.primary.main } }} />}
                  label={range}
                />
              ))}
            </RadioGroup>
            {errors.child_age_range && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.child_age_range}
              </Typography>
            )}
          </FormControl>
          
          <FormControl required error={!!errors.adults_accompanying} sx={{ bgcolor: "white", borderRadius: 1, p: 1 }}>
            <FormLabel sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PeopleIcon sx={{ color: colors.primary.main }} />
              How many adults will accompany your children?
            </FormLabel>
            <RadioGroup
              value={formData.adults_accompanying}
              onChange={handleChange("adults_accompanying")}
              row={!isMobile}
              sx={{ flexWrap: "wrap", mt: 1 }}
            >
              {adultOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio sx={{ color: colors.primary.main, "&.Mui-checked": { color: colors.primary.main } }} />}
                  label={option === "5+" ? "5+" : option}
                />
              ))}
            </RadioGroup>
            {errors.adults_accompanying && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errors.adults_accompanying}
              </Typography>
            )}
          </FormControl>
          
          <Paper sx={{ p: 2, bgcolor: colors.primary.light, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Order Summary
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="body2">Tickets:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {ticketDetails?.quantity || 0} × ₦{(ticketDetails?.ticketPrice || 0).toLocaleString()}
              </Typography>
            </Box>
            {(ticketDetails?.discount || 0) > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body2" color="success.main">Discount:</Typography>
                <Typography variant="body2" color="success.main">-₦{(ticketDetails?.discount || 0).toLocaleString()}</Typography>
              </Box>
            )}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="subtitle1" fontWeight={700}>Total to Pay:</Typography>
              <Typography variant="subtitle1" fontWeight={800} sx={{ color: colors.primary.main }}>
                ₦{(ticketDetails?.total || 0).toLocaleString()}
              </Typography>
            </Box>
          </Paper>
          
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading || !ticketDetails}
            sx={{
              bgcolor: colors.primary.main,
              py: isMobile ? 1.5 : 1.5,
              borderRadius: 3,
              fontWeight: 700,
              fontSize: isMobile ? "0.9rem" : "1rem",
              "&:hover": { bgcolor: colors.primary.dark },
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Proceed to Payment"}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

// Success Page Component
const SuccessPage = ({ bookingRef, ticketNumber, onClose }) => {
  return (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <CheckCircleIcon sx={{ fontSize: 80, color: "#4CAF50", mb: 2 }} />
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        Payment Successful! 🎉
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Your ticket has been booked successfully.
      </Typography>
      
      <Paper sx={{ p: 3, bgcolor: colors.primary.light, borderRadius: 3, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">Booking Reference</Typography>
        <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "monospace", mb: 2 }}>
          {bookingRef}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">Ticket Number</Typography>
        <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "monospace", color: colors.primary.main }}>
          {ticketNumber}
        </Typography>
      </Paper>
      
      <Alert severity="info" sx={{ mb: 3, textAlign: "left" }}>
        A confirmation email with your ticket has been sent to your email address. 
        Please check your inbox (and spam folder).
      </Alert>
      
      <Button
        variant="contained"
        onClick={onClose}
        sx={{
          bgcolor: colors.primary.main,
          "&:hover": { bgcolor: colors.primary.dark },
        }}
      >
        Close
      </Button>
    </Box>
  );
};

const EventBookingPage = () => {
  const [loading, setLoading] = useState(true);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [attendeeDialogOpen, setAttendeeDialogOpen] = useState(false);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Encode the address for Google Maps URL
  const eventAddress = "1 Bria Street, Off Ademola Adetokunbo Way, Wuse 2, Abuja";
  const encodedAddress = encodeURIComponent(eventAddress);
  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
  const appleMapsUrl = `https://maps.apple.com/?daddr=${encodedAddress}`;

  const handleGetDirections = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const url = isIOS ? appleMapsUrl : googleMapsUrl;
    window.open(url, "_blank");
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  // Check for payment callback on page load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const reference = urlParams.get('ref');
    const ticket = urlParams.get('ticket');
    
    if (reference && ticket) {
      verifyPayment(reference, ticket);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const verifyPayment = async (reference, ticketNumber) => {
    try {
      console.log("Verifying payment with reference:", reference);
      const response = await fetch(`${API_BASE_URL}/verify_payment.php?reference=${reference}`);
      const result = await response.json();
      console.log("Verification response:", result);
      
      if (result.success && result.data.payment_status === 'paid') {
        setSuccessData({
          bookingRef: result.data.booking_reference,
          ticketNumber: result.data.ticket_number || ticketNumber,
        });
        setShowSuccess(true);
        setSnackbar({
          open: true,
          message: "Payment successful! Your ticket has been booked.",
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Payment verification error:", error);
    }
  };

  const handleGetTicketClick = () => {
    setTicketDialogOpen(true);
  };

  const handleTicketConfirm = (details) => {
    console.log("Ticket details received:", details);
    setTicketDetails(details);
    setTicketDialogOpen(false);
    setTimeout(() => {
      setAttendeeDialogOpen(true);
    }, 100);
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setSuccessData(null);
  };

  if (loading) {
    return <ElegantePreloader />;
  }

  return (
    <Box sx={{ bgcolor: colors.background.soft, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header with Logo */}
      <Box sx={{ borderBottom: "1px solid #E0E0E0", py: 2, bgcolor: "white", position: "sticky", top: 0, zIndex: 10 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box
              component="img"
              src={logo}
              alt="Eleganté"
              sx={{ height: 48, objectFit: "contain" }}
              onError={(e) => {
                e.target.style.display = "none";
                const fallback = document.createElement("div");
                fallback.innerHTML = '<span style="font-size: 1.5rem; font-weight: 800;"><span style="color: #B22222;">Eleganté</span><span style="color: #000;">.</span></span>';
                e.target.parentElement.appendChild(fallback);
              }}
            />
            <Button
              variant="outlined"
              startIcon={<PhoneIcon />}
              href="tel:+2347068686839"
              sx={{
                borderColor: colors.primary.main,
                color: colors.primary.main,
                borderRadius: "8px",
                textTransform: "none",
                px: 3,
                "&:hover": {
                  borderColor: colors.primary.dark,
                  backgroundColor: alpha(colors.primary.main, 0.05),
                },
              }}
            >
              +234 706 868 6839
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 3, md: 5 },
            alignItems: "center",
            mb: 5,
          }}
        >
          <Box
            sx={{
              flex: 1,
              width: "100%",
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: "0 20px 35px -10px rgba(0,0,0,0.15)",
              transition: "transform 0.3s ease",
              "&:hover": { transform: "scale(1.01)" },
            }}
          >
            <img
              src={bannerImage}
              alt="Children's Day Fiesta"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentElement.style.background = colors.primary.gradient;
                e.target.parentElement.style.display = "flex";
                e.target.parentElement.style.alignItems = "center";
                e.target.parentElement.style.justifyContent = "center";
                e.target.parentElement.style.minHeight = "300px";
              }}
            />
          </Box>

          <Box sx={{ flex: 1, width: "100%" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "2rem", md: "2.5rem" },
                color: "#1A1A1A",
                mb: 0.5,
                lineHeight: 1.2,
              }}
            >
              Children's Day Fiesta 2026
            </Typography>
            
            <Typography
              variant="subtitle1"
              sx={{
                color: colors.primary.main,
                fontWeight: 500,
                mb: 2,
                letterSpacing: "0.5px",
              }}
            >
              Celebrating Every Child's Smile ✨
            </Typography>

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleGetTicketClick}
              sx={{
                bgcolor: colors.primary.main,
                py: 1.5,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "1rem",
                mb: 2.5,
                "&:hover": {
                  bgcolor: colors.primary.dark,
                  transform: "translateY(-2px)",
                  boxShadow: `0 8px 20px ${colors.primary.glow}`,
                },
                transition: "all 0.3s ease",
              }}
            >
              GET A TICKET
            </Button>

            <Divider sx={{ my: 1.5 }} />

            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: 2, 
                  bgcolor: colors.primary.light,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <CalendarIcon sx={{ color: colors.primary.main, fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}>
                    DATE
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    27 May 2026
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: 2, 
                  bgcolor: colors.primary.light,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <TimeIcon sx={{ color: colors.primary.main, fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}>
                    TIME
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    10:00 AM - 6:00 PM
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ 
                  width: 36, 
                  height: 36, 
                  borderRadius: 2, 
                  bgcolor: colors.primary.light,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <LocationIcon sx={{ color: colors.primary.main, fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}>
                    LOCATION
                  </Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
                    1 Bria Street, Off Ademola Adetokunbo Way, Wuse 2, Abuja
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<MapIcon />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    onClick={handleGetDirections}
                    sx={{
                      color: colors.primary.main,
                      textTransform: "none",
                      p: 0,
                      minWidth: "auto",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Get Directions
                  </Button>
                </Box>
              </Box>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: "1.1rem" }}>
              About this Event
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Get ready for a fun-filled celebration at the Children's Day Fiesta 2026!
              Join us for an exciting day dedicated to children, packed with amazing activities,
              games, entertainment, and unforgettable memories for the whole family.
            </Typography>
          </Box>
        </Box>

        {bookingConfirmed && (
          <Box
            sx={{
              maxWidth: "700px",
              mx: "auto",
              mt: 3,
              p: 2.5,
              bgcolor: "#E8F5E9",
              borderRadius: 3,
              textAlign: "center",
              border: "1px solid #4CAF50",
            }}
          >
            <CheckCircleIcon sx={{ color: "#4CAF50", fontSize: 36, mb: 0.5 }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: "#2E7D32" }}>
              Booking Reserved!
            </Typography>
            <Typography variant="body2">
              {bookingConfirmed.quantity} ticket(s) for ₦{bookingConfirmed.total.toLocaleString()}
              {bookingConfirmed.discountCode && ` with discount code ${bookingConfirmed.discountCode}`}
            </Typography>
          </Box>
        )}
      </Container>

      {/* Footer */}
      <Box
        sx={{
          bgcolor: "#1A1A1A",
          color: "#FFFFFF",
          py: 3,
          mt: "auto",
          borderTop: `3px solid ${colors.primary.main}`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#AAAAAA",
              }}
            >
              Designed by
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                background: colors.primary.gradient,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                letterSpacing: "0.5px",
              }}
            >
              Eleganté IT
            </Typography>
            <Typography
              variant="body2"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#AAAAAA",
              }}
            >
              <CopyrightIcon sx={{ fontSize: 14 }} />
              {new Date().getFullYear()}
            </Typography>
          </Box>
        </Container>
      </Box>
      
      {/* Dialogs */}
      <TicketDialog
        open={ticketDialogOpen}
        onClose={() => setTicketDialogOpen(false)}
        onConfirm={handleTicketConfirm}
      />
      
      <AttendeeFormDialog
        open={attendeeDialogOpen}
        onClose={() => setAttendeeDialogOpen(false)}
        ticketDetails={ticketDetails}
      />
      
      {/* Success Dialog */}
      <Dialog
        open={showSuccess}
        onClose={handleCloseSuccess}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 2 }
        }}
      >
        <DialogContent>
          {successData && (
            <SuccessPage
              bookingRef={successData.bookingRef}
              ticketNumber={successData.ticketNumber}
              onClose={handleCloseSuccess}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EventBookingPage;